/**
 * Unified Nova visual system for /demo/fertility/* and /demo/trying-to-conceive/*.
 * Anatomy: [Orb] [Nova] [Context pill] → body → action (right-aligned when present).
 */
import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { layoutMobileMediaQuery } from '../designSystem';

const NAME_COLOR = '#993556';
const BODY_LARGE = '#4b1528';

export const NOVA_UNIFIED_GRADIENT = 'linear-gradient(135deg, #fbeaf0 0%, #fffcf9 100%)';

export function NovaOrb({ size = 'large' }) {
  const dim = { large: 28, medium: 24, small: 20 }[size];
  const fs = { large: 11, medium: 10, small: 9 }[size];
  const ring = { large: '3.5px', medium: '3px', small: '2.5px' }[size];
  return (
    <div
      style={{
        width: dim,
        height: dim,
        minWidth: dim,
        minHeight: dim,
        borderRadius: '50%',
        background: 'var(--white, #fffcf9)',
        color: '#993556',
        boxShadow: `#d4537e 0px 0px 0px 1.5px, #fffcf9 0px 0px 0px ${ring}`,
        fontFamily: '"Playfair Display", Georgia, serif',
        fontStyle: 'italic',
        fontWeight: 900,
        fontSize: fs,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      N
    </div>
  );
}

export function NovaContextPill({ children, size = 'default', wrap = false }) {
  const xs = size === 'xs';
  return (
    <span
      style={{
        fontSize: xs ? 9 : 10,
        fontWeight: 600,
        padding: xs ? '1px 7px' : '2px 8px',
        borderRadius: 20,
        background: 'rgba(212, 83, 126, 0.13)',
        color: '#993556',
        whiteSpace: wrap ? 'normal' : 'nowrap',
        lineHeight: 1.4,
        minWidth: 0,
      }}
    >
      {children}
    </span>
  );
}

const actionLinkStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#993556',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  flexShrink: 0,
  background: 'none',
  border: 'none',
  fontFamily: 'inherit',
  padding: '0 4px 0 0',
};

/** Page-level bar (home, health shell, appointment-prep, etc.) — large orb, 14px radius */
export function NovaUnifiedPageBar({
  layout = 'stacked',
  contextPill,
  body,
  actionLabel = 'Open Nova →',
  onAction,
  extraBelow,
  /** e.g. dismiss control on sticky strip — rendered after the action link */
  trailingAfterAction,
}) {
  const compact = useMediaQuery(layoutMobileMediaQuery);
  const bodyContent =
    typeof body === 'string' ? (
      <div style={{ fontSize: 13, color: BODY_LARGE, lineHeight: 1.55 }}>{body}</div>
    ) : (
      body
    );

  const outerBase = {
    background: NOVA_UNIFIED_GRADIENT,
    border: '0.5px solid #f4c0d1',
    borderLeft: '3px solid #993556',
    borderRadius: 14,
    padding: '13px 16px',
  };

  const actionEl =
    onAction && actionLabel ? (
      <button type="button" onClick={onAction} style={actionLinkStyle}>
        {actionLabel}
      </button>
    ) : null;

  /** Narrow screens: header row + full-width message + actions — avoids horizontal overflow on the presence strip */
  if (layout === 'inline' && compact) {
    return (
      <div style={{ ...outerBase, maxWidth: '100%', boxSizing: 'border-box' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0, minWidth: 0 }}>
            <NovaOrb size="large" />
            <span style={{ fontSize: 13, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
            {contextPill ? <NovaContextPill wrap>{contextPill}</NovaContextPill> : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 'auto' }}>
            {actionEl}
            {trailingAfterAction || null}
          </div>
        </div>
        <div style={{ minWidth: 0, fontSize: 13, color: BODY_LARGE, lineHeight: 1.55, wordBreak: 'break-word' }}>{bodyContent}</div>
      </div>
    );
  }

  /** Sticky / single-line: [Orb · Nova · pill] then body (flex) then action — 7px between orb/name/pill */
  if (layout === 'inline') {
    return (
      <div
        style={{
          ...outerBase,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
          <NovaOrb size="large" />
          <span style={{ fontSize: 13, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
          {contextPill ? <NovaContextPill>{contextPill}</NovaContextPill> : null}
        </div>
        <div style={{ flex: '1 1 120px', minWidth: 0, fontSize: 13, color: BODY_LARGE, lineHeight: 1.55 }}>{bodyContent}</div>
        {actionEl}
        {trailingAfterAction || null}
      </div>
    );
  }

  /** Stacked: row1 orb+Nova+pill (mb 6), then body; action column right */
  return (
    <div
      style={{
        ...outerBase,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
          <NovaOrb size="large" />
          <span style={{ fontSize: 13, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
          {contextPill ? <NovaContextPill>{contextPill}</NovaContextPill> : null}
        </div>
        {bodyContent}
        {extraBelow || null}
      </div>
      {actionEl}
    </div>
  );
}

/** Key numbers / lab inline analysis — medium orb, 10px radius */
export function NovaUnifiedInlineAnalysis({ compact, children, style: outerStyle }) {
  return (
    <div
      style={{
        marginTop: compact ? 6 : 8,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 12px',
        background: NOVA_UNIFIED_GRADIENT,
        border: '0.5px solid #f4c0d1',
        borderLeft: '3px solid #993556',
        borderRadius: 10,
        ...outerStyle,
      }}
    >
      <NovaOrb size="medium" />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

/** Header row only (Nova + pill) for medium blocks — use inside InlineAnalysis */
export function NovaUnifiedMediumHeader({ contextPill }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
      <NovaContextPill>{contextPill}</NovaContextPill>
    </div>
  );
}

/** Appointment-prep style question tag — small orb, 8px radius, horizontal */
export function NovaUnifiedQuestionTag({ contextPill }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 10px',
        background: NOVA_UNIFIED_GRADIENT,
        border: '0.5px solid #f4c0d1',
        borderLeft: '3px solid #993556',
        borderRadius: 8,
      }}
    >
      <NovaOrb size="small" />
      <span style={{ fontSize: 11, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
      <NovaContextPill size="xs">{contextPill}</NovaContextPill>
    </div>
  );
}

/** Community thread reply — medium (inside feed); timestamp after pill */
export function NovaUnifiedCommunityReply({
  contextPill = 'Community guide',
  timestamp = 'just now',
  children,
  footer,
}) {
  return (
    <div
      style={{
        background: NOVA_UNIFIED_GRADIENT,
        border: '0.5px solid #f4c0d1',
        borderLeft: '3px solid #993556',
        borderRadius: 10,
        padding: '10px 12px',
        margin: '8px 0',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <NovaOrb size="medium" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: NAME_COLOR }}>Nova</span>
          <NovaContextPill>{contextPill}</NovaContextPill>
          {timestamp ? (
            <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 2 }}>{timestamp}</span>
          ) : null}
        </div>
        {children}
        {footer || null}
      </div>
    </div>
  );
}

/** Map raw context strings from configs to canonical pill labels */
export function novaContextPillFromRaw(ctx) {
  if (ctx == null || String(ctx).trim() === '') return 'Guidance';
  const raw = String(ctx).trim();
  const lower = raw.toLowerCase();
  const map = {
    'daily check-in': 'Daily check-in',
    'clinic visit': 'Clinic visit',
    guidance: 'Guidance',
    'morning briefing': 'Morning briefing',
    'morning check-in': 'Morning briefing',
    'morning briefng': 'Morning briefing',
    'afternoon check-in': 'Daily check-in',
    'evening check-in': 'Daily check-in',
    'check-in': 'Daily check-in',
    'fertile window check-in': 'Daily check-in',
    'here to help': 'Guidance',
    'morning nudge': 'Morning briefing',
    'evening reminder': 'Daily check-in',
    'checking in': 'Guidance',
    tonight: 'Guidance',
    'on your labs': 'Guidance',
    'cost alert': 'Guidance',
    'appointment prep': 'Clinic visit',
    'medication reminder': 'Guidance',
    'community guide': 'Community guide',
    'matching now': 'Guidance',
    'inclusive support': 'Inclusive support',
    'partner support': 'Partner support',
    partner: 'Partner support',
    learning: 'Guidance',
    'loss support': 'Guidance',
    'today for you': 'Today for you',
    analysis: 'Analysis',
  };
  if (map[lower]) return map[lower];
  return raw;
}

export { actionLinkStyle as novaUnifiedActionLinkStyle, BODY_LARGE as novaUnifiedBodyColor };
